import React from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, Star } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { mdxModules } from "../content";
import { useIsMobile } from "../hooks/use-mobile";

export interface BlogPost {
  id: string;
  title: string;
  excerpt?: string;
  date: string;
  category: string;
  readTime?: string;
  author: string;
  imageUrl?: string;
  content?: string;
  MDXContent?: React.ComponentType;
  featured?: boolean;
  draft?: boolean;
}

interface BlogCardProps {
  post: BlogPost;
  className?: string;
}

const BlogCard = ({ post, className }: BlogCardProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Optimize the intersection observer by using a ref
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Skip for server-side rendering
    if (typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (mdxModules[post.id]) {
            mdxModules[post.id]();
          }
          observer.disconnect();
        }
      },
      { rootMargin: "100px 0px" } // Reduced from 200px to 100px for faster loading
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [post.id]);

  const handleHover = React.useCallback(() => {
    // Preload content only when actually hovering, not on mount
    if (mdxModules[post.id]) {
      mdxModules[post.id]();
    }

    if (post.imageUrl) {
      const img = new Image();
      img.src = post.imageUrl;
      img.fetchPriority = "high";
    }

    // Only set hover state on desktop
    if (!isMobile) {
      setIsHovered(true);
    }
  }, [post.id, post.imageUrl, isMobile]);

  // On mobile devices, we need to ensure navigation happens immediately
  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Force immediate navigation to blog post
    if (post.id) {
      navigate(`/blog/${post.id}`);
    }
  };

  return (
    <div
      ref={cardRef}
      id={`blog-card-${post.id}`}
      className={cn(
        "border border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-200 overflow-hidden relative cursor-pointer",
        !isMobile && isHovered ? "transform hover:-translate-y-1" : "",
        className
      )}
      onMouseEnter={handleHover}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
      role="link"
      aria-label={`Read ${post.title}`}
    >
      {post.featured && (
        <div className="absolute top-2 right-2 z-10 flex items-center">
          {post.readTime && (
            <span className="text-xs text-muted-foreground mr-2">
              {post.readTime} min read
            </span>
          )}
          <div
            className={cn(
              "bg-primary/90 text-primary-foreground rounded-tr-md rounded-bl-md p-1 shadow-sm",
              !isMobile && isHovered ? "animate-pulse" : ""
            )}
          >
            <Star size={12} className="text-primary-foreground" />
          </div>
        </div>
      )}
      <div className="p-3 flex flex-col flex-1">
        <div className="flex flex-wrap items-center justify-between text-sm mb-0 gap-2">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{post.date}</span>
            <div
              className={cn(
                "bg-secondary/30 text-secondary-foreground px-2 py-0.5 rounded-full text-xs font-medium",
                !isMobile && isHovered ? "bg-secondary/50" : ""
              )}
            >
              {post.category}
            </div>
          </div>
          {!post.featured && post.readTime && (
            <span className="text-muted-foreground text-xs">
              {post.readTime} min read
            </span>
          )}
        </div>
        <h3
          className={cn(
            "text-base font-semibold text-foreground mb-1",
            !isMobile && isHovered ? "text-primary" : ""
          )}
        >
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-muted-foreground text-sm mb-3 flex-1 font-['Inter',sans-serif] leading-relaxed">
            {post.excerpt}
          </p>
        )}

        {/* For mobile, use a simple div indicator instead of a link */}
        {isMobile ? (
          <div
            className="read-more-link flex items-center text-sm font-medium text-primary mt-auto bg-primary/5 px-2 py-1 rounded-md w-fit"
            aria-hidden="true" // Make this non-interactive since the whole card is clickable
          >
            Read more
            <ArrowRight size={12} className="ml-1" />
          </div>
        ) : (
          <Link
            to={`/blog/${post.id}`}
            className={cn(
              "read-more-link flex items-center text-sm font-medium text-primary hover:text-primary/80 mt-auto bg-primary/5 px-2 py-1 rounded-md w-fit",
              isHovered ? "bg-primary/15 transform translate-x-1" : "",
              "transition-all duration-200"
            )}
            onMouseEnter={handleHover}
            onFocus={handleHover}
            onClick={(e) => {
              // Prevent event bubbling on desktop to avoid double navigation
              if (!isMobile) {
                e.stopPropagation();
              }
            }}
          >
            Read more
            <ArrowRight
              size={12}
              className={cn(
                "ml-1",
                isHovered ? "transform translate-x-1" : "",
                "transition-transform duration-200"
              )}
            />
          </Link>
        )}
      </div>
    </div>
  );
};

export default BlogCard;
